import { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import * as RD from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { MarketQuote, PlayerSearchResult, Team } from '../lib/types';

export type SearchPage = { to: string; label: string; section: string };

/**
 * ⌘K search: jump to any page, this week's games, a team's roster, or a
 * player. Games and teams load once when the palette first opens; players
 * are searched as you type.
 */
export function CommandSearch({ open, onOpenChange, pages }: { open: boolean; onOpenChange: (v: boolean) => void; pages: SearchPage[] }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [games, setGames] = useState<MarketQuote[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<PlayerSearchResult[]>([]);

  useEffect(() => {
    if (!open || games.length) return;
    api.markets().then((b) => setGames(b.quotes)).catch(() => {});
    api.teams().then((t) => setTeams(t.teams)).catch(() => {});
  }, [open, games.length]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setPlayers([]);
      return;
    }
    const t = setTimeout(() => {
      api.fantasy.search(q.trim()).then((r) => setPlayers(r.players.slice(0, 8))).catch(() => setPlayers([]));
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const go = (to: string) => {
    onOpenChange(false);
    setQ('');
    navigate(to);
  };

  const sortedTeams = useMemo(() => [...teams].sort((a, b) => a.displayName.localeCompare(b.displayName)), [teams]);

  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="ui-dialog-overlay" />
        <RD.Content className="cmdk-dialog" aria-label="Search">
          <RD.Title className="sr-only">Search</RD.Title>
          <RD.Description className="sr-only">Search teams, players, games and pages</RD.Description>
          <Command label="Search" shouldFilter>
            <Command.Input value={q} onValueChange={setQ} placeholder="Search teams, players, props…" className="cmdk-input" autoFocus />
            <Command.List className="cmdk-list">
              <Command.Empty className="cmdk-empty">No matches.</Command.Empty>
              {games.length > 0 && (
                <Command.Group heading="This week's games" className="cmdk-group">
                  {games.map((g) => (
                    <Command.Item
                      key={g.gameId}
                      value={`${g.away.abbr} ${g.home.abbr} ${g.away.name} ${g.home.name} game`}
                      onSelect={() => go(`/game/${g.gameId}`)}
                      className="cmdk-item"
                    >
                      <span>{g.away.abbr} @ {g.home.abbr}</span>
                      <span className="cmdk-meta num">
                        {g.spread != null ? `${g.home.abbr} ${g.spread > 0 ? '+' : ''}${g.spread}` : ''}
                        {g.total != null ? ` · ${g.total}` : ''}
                      </span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
              {players.length > 0 && (
                <Command.Group heading="Players" className="cmdk-group">
                  {players.map((p) => (
                    <Command.Item
                      key={p.id}
                      // Already matched by the server; keep them visible whatever cmdk scores.
                      value={`${q} ${p.name} ${p.teamAbbr} ${p.position} player`}
                      onSelect={() => go(`/props?q=${encodeURIComponent(p.name)}`)}
                      className="cmdk-item"
                    >
                      <span>{p.name}</span>
                      <span className="cmdk-meta">{p.position} · {p.teamAbbr}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
              <Command.Group heading="Teams" className="cmdk-group">
                {sortedTeams.map((t) => (
                  <Command.Item key={t.id} value={`${t.displayName} ${t.abbreviation} team roster`} onSelect={() => go(`/rosters/${t.id}`)} className="cmdk-item">
                    <span>{t.displayName}</span>
                    <span className="cmdk-meta">{t.abbreviation}</span>
                  </Command.Item>
                ))}
              </Command.Group>
              <Command.Group heading="Pages" className="cmdk-group">
                {pages.map((p) => (
                  <Command.Item key={p.to} value={`${p.label} ${p.section} page`} onSelect={() => go(p.to)} className="cmdk-item">
                    <span>{p.label}</span>
                    <span className="cmdk-meta">{p.section}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>
          </Command>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
